/**
 * Condition catalog for the "Who We Support" section popups
 * (SupportSection.jsx). Keys mirror the ``label`` field on every entry in
 * ``SUPPORT_PRIMARY`` and ``SUPPORT_SECONDARY`` in
 * ``pages/landing/SupportSection.jsx``, so the click-handler can look up
 * the right entry by the same string the card already shows.
 *
 * If you rename a label in SupportSection.jsx you MUST update the key
 * here too — there's no fallback prose. A missing key is treated as a
 * click bug and the dialog stays closed.
 */
export const CONDITION_DETAILS = {
  "Autism Spectrum Disorder": {
    title: "Autism (ASD)",
    whatIsHeading: "What is Autism (ASD)?",
    whatIsBody:
      "Autism Spectrum Disorder (ASD) is a neurodevelopmental condition that affects how a child communicates, interacts with others, learns, and experiences the world. Every autistic child is unique. Some children may have strong language skills, while others may communicate using gestures, pictures, or assistive devices. They may also have sensory sensitivities, repetitive behaviours, or prefer predictable routines.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ helps parents better understand their child's strengths and support needs through screening tools, personalised activities, progress tracking, and practical guidance. Activities are tailored to communication, sensory, motor, and daily living skills, helping families build meaningful skills through everyday play and routines.",
  },
  "Attention-Deficit/Hyperactivity Disorder": {
    title: "ADHD",
    whatIsHeading: "What is ADHD?",
    whatIsBody:
      "Attention-Deficit/Hyperactivity Disorder (ADHD) affects a child's ability to focus, regulate impulses, and manage activity levels. Children may appear inattentive, constantly moving, easily distracted, or act before thinking. These behaviours can impact learning, friendships, and daily routines.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ provides personalised activities to improve attention, self-regulation, executive functioning, and emotional control. Parents receive practical strategies that can be used at home while tracking progress over time.",
  },
  "Learning Disabilities": {
    title: "Learning Disabilities",
    whatIsHeading: "What are Learning Disabilities?",
    whatIsBody:
      "Learning disabilities affect how children process information. A child may have difficulty reading, writing, spelling, mathematics, memory, or understanding instructions despite having average intelligence.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ identifies learning-related challenges through developmental screening and provides targeted activities that strengthen attention, memory, problem-solving, visual perception, and foundational learning skills.",
  },
  "Speech & Language Delays": {
    title: "Speech & Language",
    whatIsHeading: "What is Speech & Language Delay?",
    whatIsBody:
      "Speech difficulties involve producing sounds clearly, while language difficulties affect understanding or using words to communicate. Some children may speak late, have unclear speech, or struggle to express their thoughts.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ offers communication-focused activities that encourage language development, vocabulary building, listening skills, and interaction. Parents also receive practical communication strategies to use during everyday routines.",
  },
  "Social Communication Difficulties": {
    title: "Social Communication",
    whatIsHeading: "What are Social Communication Difficulties?",
    whatIsBody:
      "Some children find it difficult to understand social rules, maintain conversations, interpret facial expressions, or use appropriate body language. These challenges may affect friendships and participation in group activities.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ provides activities that strengthen conversation skills, turn-taking, eye contact, emotional understanding, and social interaction through structured play and real-life practice.",
  },
  "Intellectual & Developmental Disabilities": {
    title: "Intellectual & Developmental Disabilities",
    whatIsHeading: "What are Intellectual & Developmental Disabilities?",
    whatIsBody:
      "These conditions affect intellectual functioning and adaptive skills such as communication, learning, self-care, and independent living. Children often learn new skills at a slower pace and may need additional support.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ delivers personalised developmental activities across multiple domains while helping parents build daily routines that encourage independence and consistent progress.",
  },
  "Sensory Processing Difficulties": {
    title: "Sensory Processing Difficulties",
    whatIsHeading: "What are Sensory Processing Difficulties?",
    whatIsBody:
      "Some children process sensory information differently. They may be overly sensitive or under-responsive to sounds, touch, movement, lights, textures, smells, or taste, affecting behaviour, attention, and daily activities.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ recommends sensory-friendly activities that help children regulate their responses, improve attention, participate more comfortably in daily routines, and develop sensory processing skills.",
  },
  "Developmental Coordination Disorder (Dyspraxia)": {
    title: "Dyspraxia",
    whatIsHeading: "What is Dyspraxia?",
    whatIsBody:
      "Developmental Coordination Disorder affects planning, coordinating, and performing motor movements. Children may appear clumsy, struggle with sports, dressing, handwriting, or everyday physical tasks.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ provides step-by-step motor activities that improve coordination, balance, motor planning, strength, and confidence through enjoyable daily practice.",
  },
  "Fine Motor & Handwriting Challenges": {
    title: "Fine Motor & Handwriting",
    whatIsHeading: "What are Fine Motor Challenges?",
    whatIsBody:
      "Fine motor skills involve using the small muscles of the hands and fingers. Difficulties may affect writing, drawing, buttoning clothes, using scissors, or handling small objects.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ offers age-appropriate hand strengthening, finger coordination, pre-writing, and handwriting readiness activities to improve functional independence.",
  },
  "Gross Motor Delays": {
    title: "Gross Motor Delays",
    whatIsHeading: "What are Gross Motor Delays?",
    whatIsBody:
      "Gross motor skills involve large body movements such as walking, running, jumping, climbing, balancing, and coordination. Delays can affect participation in play and physical activities.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ recommends movement-based activities that strengthen balance, coordination, posture, muscle control, and overall physical development.",
  },
  "Executive Function Difficulties": {
    title: "Executive Function",
    whatIsHeading: "What are Executive Function Difficulties?",
    whatIsBody:
      "Executive functions help children plan, organise, remember instructions, manage time, solve problems, and control their behaviour. Difficulties can affect school performance and daily routines.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ includes activities that strengthen planning, sequencing, memory, organisation, flexible thinking, and self-management skills.",
  },
  "Emotional Regulation Challenges": {
    title: "Emotional Regulation",
    whatIsHeading: "What are Emotional Regulation Challenges?",
    whatIsBody:
      "Some children struggle to recognise, express, or manage emotions. They may have frequent meltdowns, frustration, anxiety, or difficulty calming down after becoming upset.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ teaches calming strategies, emotional awareness activities, and practical techniques that help children recognise emotions and develop healthy coping skills.",
  },
  "Behavioural Difficulties": {
    title: "Behavioural Difficulties",
    whatIsHeading: "What are Behavioural Difficulties?",
    whatIsBody:
      "Behavioural challenges may include frequent tantrums, aggression, non-compliance, impulsive actions, or difficulty following routines. Behaviour often reflects underlying developmental, communication, sensory, or emotional needs.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ helps parents understand possible reasons behind behaviours and provides structured activities and positive behaviour strategies that encourage better self-regulation.",
  },
  "Oppositional Defiant Disorder (ODD)": {
    title: "ODD",
    whatIsHeading: "What is ODD?",
    whatIsBody:
      "ODD is characterised by frequent anger, arguing with adults, refusing to follow rules, and deliberately challenging authority beyond what is expected for a child's age.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ focuses on improving emotional regulation, communication, routine building, and positive parent-child interactions while supporting the development of self-control skills.",
  },
  "Global Developmental Delay (GDD)": {
    title: "GDD",
    whatIsHeading: "What is Global Developmental Delay?",
    whatIsBody:
      "Global Developmental Delay occurs when a young child shows significant delays across two or more developmental areas such as communication, movement, cognition, social skills, or self-care.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ provides personalised activities covering multiple developmental domains while helping parents monitor progress and support skill development at home.",
  },
  "Genetic & Rare Developmental Conditions": {
    title: "Genetic & Rare Conditions",
    whatIsHeading: "What are Genetic & Rare Developmental Conditions?",
    whatIsBody:
      "Certain genetic conditions can affect growth, learning, communication, movement, behaviour, and overall development. Every child has unique strengths and support needs.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ personalises activities according to each child's developmental profile rather than focusing only on a diagnosis, allowing families to work on meaningful functional goals.",
  },
  "Anxiety-Related Challenges": {
    title: "Anxiety-Related Challenges",
    whatIsHeading: "What are Anxiety-Related Challenges?",
    whatIsBody:
      "Children with anxiety may worry excessively, avoid certain situations, struggle with separation, or show physical symptoms like stomach aches or sleep difficulties.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ provides calming activities, structured routines, emotional regulation exercises, and parent guidance that help children build confidence and cope with everyday situations.",
  },
  "School Readiness Delays": {
    title: "School Readiness",
    whatIsHeading: "What are School Readiness Delays?",
    whatIsBody:
      "Some children may not yet have the skills needed for a successful transition to school, including attention, communication, social interaction, fine motor abilities, and independence.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ strengthens essential pre-school and classroom skills through engaging daily activities designed to build confidence before school begins.",
  },
  "Attention & Concentration Difficulties": {
    title: "Attention & Concentration",
    whatIsHeading: "What are Attention & Concentration Difficulties?",
    whatIsBody:
      "Some children find it difficult to stay focused, complete tasks, follow instructions, or ignore distractions even without an ADHD diagnosis.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ offers activities that gradually improve sustained attention, working memory, listening, and task completion through enjoyable, structured practice.",
  },
  "Self-Care & Daily Living Skill Delays": {
    title: "Self-Care & Daily Living",
    whatIsHeading: "What are Self-Care Delays?",
    whatIsBody:
      "These challenges affect everyday activities such as dressing, brushing teeth, eating independently, toileting, bathing, and organising personal belongings.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ provides practical step-by-step activities that promote independence and help children master everyday routines with greater confidence.",
  },
  "Play Skill Difficulties": {
    title: "Play Skills",
    whatIsHeading: "What are Play Skill Difficulties?",
    whatIsBody:
      "Play is essential for learning and development. Some children struggle with pretend play, sharing, taking turns, problem-solving, or engaging appropriately with toys and peers.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ introduces structured play activities that build imagination, creativity, communication, social interaction, and cognitive development.",
  },
  "Social Skills Challenges": {
    title: "Social Skills",
    whatIsHeading: "What are Social Skills Challenges?",
    whatIsBody:
      "Children may have difficulty making friends, understanding social rules, sharing, cooperating, or recognising other people's feelings.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ provides activities that develop friendship skills, empathy, cooperation, conversation, and confidence in social situations.",
  },
  "Feeding & Mealtime Difficulties": {
    title: "Feeding & Mealtime",
    whatIsHeading: "What are Feeding & Mealtime Difficulties?",
    whatIsBody:
      "Some children may be extremely selective eaters, avoid certain textures, struggle with chewing or swallowing, or experience stressful mealtimes due to sensory or developmental factors.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ offers practical feeding activities, sensory-based strategies, and mealtime routines that help children become more comfortable and confident during eating.",
  },
  "Children at Risk of Developmental Delays": {
    title: "At-Risk Children",
    whatIsHeading: "What does “At Risk” mean?",
    whatIsBody:
      "Some children may not currently have a diagnosis but show developmental concerns or have medical, genetic, or environmental factors that increase their risk of delays. Early support can make a significant difference.",
    howHeading: "How Kiddo+ Supports",
    howBody:
      "Kiddo+ helps families identify developmental concerns early through screening, personalised developmental activities, regular progress tracking, and expert-informed guidance. Early intervention gives children the best opportunity to build essential life skills and reach their full potential.",
  },
};
